/**
 * Shift Calendar — FullCalendar Resource TimeGrid
 *
 * Domain-agnostic shift scheduling calendar (DRR-232).
 * Replaces ALFShiftCalendar with configurable time boundaries.
 *
 * Resources = staff members (from backend shift data)
 * Events = scheduled shifts with status-based coloring
 *
 * Plugins: resourceTimeGrid (resource axis), interaction (click handlers),
 *   timeGrid (week/day views). All already installed.
 *
 * Data-driven: all shift data from useScheduleWeek hook — no hardcoded values.
 * Dumb frontend pattern (DRR-055).
 */

import { useMemo, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import type { EventInput, EventClickArg, DateSelectArg, EventDropArg } from '@fullcalendar/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';

// Status → FullCalendar background color (Mantine-aligned palette)
const SHIFT_STATUS_BACKGROUNDS: Record<string, string> = {
  scheduled: '#228be6',  // blue
  confirmed: '#40c057',  // green
  in_progress: '#15aabf', // cyan
  completed: '#868e96',  // gray
  cancelled: '#fa5252',  // red
  no_show: '#fd7e14',    // orange
};

const SHIFT_STATUS_BORDERS: Record<string, string> = {
  scheduled: '#1c7ed6',
  confirmed: '#37b24d',
  in_progress: '#1098ad',
  completed: '#495057',
  cancelled: '#e03131',
  no_show: '#e8590c',
};

export interface ShiftData {
  id: string;
  employeeId: string;
  employeeName?: string;
  startTime: string;
  endTime: string;
  status: string;
  branchId?: string;
  departmentId?: string;
  isLifeCritical?: boolean;
}

export interface ShiftDropInfo {
  shift: ShiftData;
  newStart: Date;
  newEnd: Date;
  newResourceId?: string;
  revert: () => void;
}

export interface ShiftCalendarProps {
  /** Shift data from backend */
  shifts: ShiftData[];
  /** Initial date to display */
  initialDate?: string;
  /** Callback when a shift event is clicked */
  onShiftClick?: (shift: ShiftData) => void;
  /** Callback when an empty time slot is selected (for adding shifts) */
  onSlotSelect?: (info: { start: Date; end: Date; resourceId?: string }) => void;
  /** Async callback when a shift is dragged to a new time/resource */
  onShiftDrop?: (info: ShiftDropInfo) => Promise<void>;
  /** View mode: day, week, or resource-day */
  initialView?: 'timeGridDay' | 'timeGridWeek' | 'resourceTimeGridDay';
  /** Enable drag-and-drop editing (default: false) */
  editable?: boolean;
  /** Slot boundaries - start time (default: "06:00:00") */
  slotMinTime?: string;
  /** Slot boundaries - end time (default: "22:00:00") */
  slotMaxTime?: string;
  /** Slot duration (default: "00:30:00") */
  slotDuration?: string;
  /** Resource column header text (default: "Staff") */
  resourceAreaHeader?: string;
  /** Resource column width (default: "120px") */
  resourceAreaWidth?: string;
  /** Default shift duration in ms for drops without end time (default: 8 hours) */
  defaultShiftDurationMs?: number;
  /** Callback to refetch data after successful drop */
  onSuccess?: () => void;
}

/**
 * FullCalendar-powered shift calendar for staffing.
 *
 * Three view modes:
 * - Day: single column time grid
 * - Week: 7 day time grid (classic week view)
 * - Staff Day: resource-timegrid with one column per staff member
 */
export function ShiftCalendar({
  shifts,
  initialDate,
  onShiftClick,
  onSlotSelect,
  onShiftDrop,
  initialView = 'resourceTimeGridDay',
  editable = false,
  slotMinTime = '06:00:00',
  slotMaxTime = '22:00:00',
  slotDuration = '00:30:00',
  resourceAreaHeader = 'Staff',
  resourceAreaWidth = '120px',
  defaultShiftDurationMs = 8 * 60 * 60 * 1000,
  onSuccess,
}: ShiftCalendarProps) {
  // Build resource list from unique employee IDs in shift data
  const resources = useMemo(() => {
    const employeeMap = new Map<string, string>();
    shifts.forEach((s) => {
      if (!employeeMap.has(s.employeeId)) {
        employeeMap.set(s.employeeId, s.employeeName || s.employeeId);
      }
    });
    return Array.from(employeeMap).map(([id, title]) => ({ id, title }));
  }, [shifts]);

  // Convert shifts to FullCalendar events
  const events: EventInput[] = useMemo(() => {
    return shifts.map((shift) => ({
      id: shift.id,
      resourceId: shift.employeeId,
      title: shift.employeeName || shift.employeeId,
      start: shift.startTime,
      end: shift.endTime,
      backgroundColor: shift.isLifeCritical
        ? '#fa5252'
        : (SHIFT_STATUS_BACKGROUNDS[shift.status] ?? '#228be6'),
      borderColor: shift.isLifeCritical
        ? '#e03131'
        : (SHIFT_STATUS_BORDERS[shift.status] ?? '#1c7ed6'),
      extendedProps: {
        shiftData: shift,
        isLifeCritical: shift.isLifeCritical ?? false,
      },
    }));
  }, [shifts]);

  const handleEventClick = useCallback(
    (info: EventClickArg) => {
      const shiftData = info.event.extendedProps?.shiftData as ShiftData | undefined;
      if (shiftData && onShiftClick) {
        onShiftClick(shiftData);
      }
    },
    [onShiftClick],
  );

  const handleSelect = useCallback(
    (info: DateSelectArg) => {
      if (onSlotSelect) {
        onSlotSelect({
          start: info.start,
          end: info.end,
          resourceId: info.resource?.id,
        });
      }
    },
    [onSlotSelect],
  );

  const handleEventDrop = useCallback(
    (info: EventDropArg) => {
      const shiftData = info.event.extendedProps?.shiftData as ShiftData | undefined;
      if (!shiftData || !onShiftDrop) {
        info.revert();
        return;
      }

      const newStart = info.event.start!;
      const newEnd = info.event.end || new Date(newStart.getTime() + defaultShiftDurationMs);
      const isLifeCritical = shiftData.isLifeCritical ?? false;

      const performDrop = async () => {
        try {
          await onShiftDrop({
            shift: shiftData,
            newStart,
            newEnd,
            newResourceId: info.newResource?.id,
            revert: info.revert,
          });
          notifications.show({
            title: 'Shift Rescheduled',
            message: `${shiftData.employeeName || shiftData.employeeId} moved to ${newStart.toLocaleString()}`,
            color: 'green',
          });
          onSuccess?.();
        } catch (err) {
          notifications.show({
            title: 'Reschedule Failed',
            message: err instanceof Error ? err.message : 'Unknown error',
            color: 'red',
          });
          info.revert();
        }
      };

      // Life-critical safety gate
      if (isLifeCritical) {
        modals.openConfirmModal({
          title: 'Life-Critical Shift',
          children: `This shift is marked as life-critical. Rescheduling may impact care coverage. Are you sure?`,
          labels: { confirm: 'Reschedule Anyway', cancel: 'Cancel' },
          confirmProps: { color: 'red' },
          onCancel: () => info.revert(),
          onConfirm: () => void performDrop(),
        });
      } else {
        void performDrop();
      }
    },
    [onShiftDrop, defaultShiftDurationMs, onSuccess],
  );

  return (
    <FullCalendar
      plugins={[resourceTimeGridPlugin, timeGridPlugin, interactionPlugin]}
      initialView={initialView}
      initialDate={initialDate}
      headerToolbar={{
        left: 'prev,next today',
        center: 'title',
        right: 'resourceTimeGridDay,timeGridWeek,timeGridDay',
      }}
      buttonText={{
        resourceTimeGridDay: 'Staff Day',
        timeGridWeek: 'Week',
        timeGridDay: 'Day',
      }}
      resources={resources}
      events={events}
      eventClick={handleEventClick}
      eventDrop={handleEventDrop}
      select={handleSelect}
      selectable={true}
      selectMirror={true}
      editable={editable}
      eventDurationEditable={false}
      height="auto"
      slotMinTime={slotMinTime}
      slotMaxTime={slotMaxTime}
      slotDuration={slotDuration}
      allDaySlot={false}
      nowIndicator={true}
      resourceAreaHeaderContent={resourceAreaHeader}
      resourceAreaWidth={resourceAreaWidth}
      eventDisplay="block"
    />
  );
}

// Re-export for backwards compatibility during migration
export { ShiftCalendar as ALFShiftCalendar };
